/**
 * FollowTheMoneySection — Per-node FTM controls, mirroring the
 * AutoInvestigateCard pattern used by "Targeted Node Intelligence".
 *
 * States:
 *   - No cached result       → description + "Follow the Money" button
 *   - Running (workbench)    → disabled button + skeleton of the result card
 *   - Cached result exists   → summary card + "View Report", "Re-run",
 *                              and the "💰 Focus" dim toggle
 *
 * The workbench owns the actual SSE/REST run; this component only
 * drives the UI and reads the cached result from useFtmStore.
 */
import React from 'react';
import { Zap, FileText, RefreshCw, Eye } from 'lucide-react';
import { useFtmStore } from '../../stores/useFtmStore';
import { safeStr } from '../../utils/safeStr';
import { Skeleton, SkeletonList } from '../ui/Skeleton';

interface Props {
    chain: string | null;
    address: string | null;
    isRunning: boolean;
    followMoneyMode: boolean;
    onRun: () => void;
    onOpenReport: () => void;
    onToggleDim: (next: boolean) => void;
}

const formatRanAt = (iso: string) => {
    try {
        const d = new Date(iso);
        const now = Date.now();
        const deltaMin = Math.round((now - d.getTime()) / 60000);
        if (deltaMin < 1) return 'just now';
        if (deltaMin < 60) return `${deltaMin}m ago`;
        if (deltaMin < 1440) return `${Math.round(deltaMin / 60)}h ago`;
        return d.toLocaleDateString();
    } catch {
        return iso;
    }
};

export const FollowTheMoneySection: React.FC<Props> = ({
    chain,
    address,
    isRunning,
    followMoneyMode,
    onRun,
    onOpenReport,
    onToggleDim,
}) => {
    const entry = useFtmStore(s => s.getEntry(chain, address));

    if (!chain || !address) {
        return (
            <div className="p-4 text-xs text-muted-foreground">
                Select a node to run Follow the Money.
            </div>
        );
    }

    // ── Cached result ───────────────────────────────────────────
    if (entry && !isRunning) {
        const paths: any[] = entry.response?.paths || entry.response?.traces || [];
        const primary = paths[0];
        const hops = primary?.addresses?.length
            ? primary.addresses.length - 1
            : primary?.path?.length
                ? primary.path.length - 1
                : (primary?.edges?.length ?? 0);
        const targetName = safeStr(primary?.target_entity?.name) || 'destination';
        const targetType = safeStr(primary?.target_entity?.type) || null;

        // Sum of values across the primary path's edges, token-agnostic.
        let totalValue = 0;
        let primaryToken = '';
        for (const e of (primary?.edges || [])) {
            if (typeof e?.value === 'number' && !isNaN(e.value)) totalValue += e.value;
            if (!primaryToken && e?.token_symbol) primaryToken = String(e.token_symbol);
        }

        return (
            <div className="p-3 space-y-3">
                <div className="text-xs text-muted-foreground">
                    Cached trail for{' '}
                    <span className="font-mono text-foreground">
                        {address.length > 16 ? `${address.slice(0, 10)}…${address.slice(-6)}` : address}
                    </span>
                    {' '}· ran {formatRanAt(entry.ranAt)}.
                </div>

                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
                    <div className="flex items-baseline justify-between">
                        <div className="text-sm font-medium text-amber-600 dark:text-amber-400">
                            {hops} hop{hops === 1 ? '' : 's'} → {targetName}
                        </div>
                        {targetType && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                                {targetType}
                            </span>
                        )}
                    </div>
                    {totalValue > 0 && (
                        <div className="text-[11px] text-muted-foreground">
                            Primary trail total:{' '}
                            <span className="text-foreground font-medium">
                                {totalValue.toLocaleString(undefined, { maximumFractionDigits: 6 })}
                            </span>{' '}
                            {primaryToken}
                        </div>
                    )}
                    {paths.length > 1 && (
                        <div className="text-[11px] text-muted-foreground">
                            {paths.length - 1} additional path{paths.length - 1 === 1 ? '' : 's'} found.
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={onOpenReport}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                    >
                        <FileText className="w-3.5 h-3.5" />
                        View Full Report
                    </button>
                    <button
                        onClick={() => onToggleDim(!followMoneyMode)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${followMoneyMode
                            ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500/40'
                            : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20'}`}
                        title="Dim non-trace elements on the canvas"
                    >
                        <Eye className="w-3.5 h-3.5" />
                        {followMoneyMode ? 'Focus: On' : 'Focus Trail'}
                    </button>
                    <button
                        onClick={onRun}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-muted text-muted-foreground hover:bg-muted/80 transition-colors ml-auto"
                        title="Re-run Follow the Money from scratch"
                    >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Re-run
                    </button>
                </div>
            </div>
        );
    }

    // ── Running ──────────────────────────────────────────────────
    if (isRunning) {
        return (
            <div className="p-3 space-y-3" role="status" aria-label="Tracing the money trail">
                <Skeleton className="h-3 w-3/4" />
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/2" />
                </div>
                <SkeletonList count={2} label="Tracing the money trail" />
            </div>
        );
    }

    // ── No cached result yet — initial state ─────────────────────
    return (
        <div className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">Follow the Money</span>{' '}
                traces the outgoing fund path from this address up to 4 hops, searching
                for the nearest KYC exchange or labelled destination. Results stay
                cached on this node and are auto-archived to the investigation.
            </p>
            <button
                onClick={onRun}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
            >
                <Zap className="w-4 h-4" />
                Follow the Money
            </button>
        </div>
    );
};
